/* Units. Everything inside the builder is millimetres; these helpers only
   convert for display and for the 300 DPI production maths. */

export type Unit = 'mm' | 'cm' | 'in';
export const MM_PER_IN = 25.4;
export const UNITS: Unit[] = ['mm', 'cm', 'in'];

export function toMm(value: number, unit: Unit): number {
  if (unit === 'cm') return value * 10;
  if (unit === 'in') return value * MM_PER_IN;
  return value;
}

export function fromMm(mm: number, unit: Unit): number {
  if (unit === 'cm') return mm / 10;
  if (unit === 'in') return mm / MM_PER_IN;
  return mm;
}

/** Sensible input step / decimals for each unit. */
export function unitDecimals(unit: Unit): number {
  return unit === 'mm' ? 0 : unit === 'cm' ? 1 : 2;
}

export function roundTo(v: number, decimals: number): number {
  const f = Math.pow(10, decimals);
  return Math.round(v * f) / f;
}

/** "128 mm", "12.8 cm", "5.04 in" */
export function formatLength(mm: number, unit: Unit, decimals = unitDecimals(unit)): string {
  const v = roundTo(fromMm(mm, unit), decimals);
  return trimZeros(v.toFixed(decimals)) + (unit === 'in' ? ' in' : ' ' + unit);
}

/** "58 × 134 cm" */
export function formatDims(wMm: number, hMm: number, unit: Unit, decimals = unitDecimals(unit)): string {
  const w = trimZeros(roundTo(fromMm(wMm, unit), decimals).toFixed(decimals));
  const h = trimZeros(roundTo(fromMm(hMm, unit), decimals).toFixed(decimals));
  return w + ' × ' + h + (unit === 'in' ? ' in' : ' ' + unit);
}

/** Length of film in metres for headlines: "2.3 m" */
export function formatMetres(mm: number): string {
  return trimZeros((Math.round(mm / 10) / 100).toFixed(2)) + ' m';
}

export function trimZeros(s: string): string {
  return s.indexOf('.') >= 0 ? s.replace(/\.?0+$/, '') : s;
}

/** Physical millimetres → pixels at a given DPI (unrounded). */
export function mmToPx(mm: number, dpi: number): number {
  return (mm / MM_PER_IN) * dpi;
}

/** Pixels at a given DPI → millimetres. */
export function pxToMm(px: number, dpi: number): number {
  return (px / dpi) * MM_PER_IN;
}

/** The whole-pixel size the production renderer uses for a physical size. */
export function productionPx(mm: number, dpi: number): number {
  return Math.max(1, Math.round(mmToPx(mm, dpi)));
}

/** PNG pHYs value: pixels per metre for a DPI (300 DPI → 11811). */
export function dpiToPixelsPerMetre(dpi: number): number {
  return Math.round(dpi / 0.0254);
}

export function formatMoney(v: number, currency = 'NZD'): string {
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  const fixed = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return sign + (currency === 'NZD' || currency === 'AUD' || currency === 'USD' ? '$' : currency + ' ') + fixed;
}
